/**
 * Shared icon-button primitives — used by the Dashboard and Project page
 * so management affordances (add / rename / delete / save / cancel) look
 * and feel identical everywhere: round, comfortably tappable, clearly a
 * button (filled-accent / hairline / destructive-tinted), with a pressed
 * scale. Icons come from `components/icons.tsx`.
 */
import { Link } from "react-router-dom";

export type BtnVariant = "default" | "primary" | "danger";

const BASE =
  "inline-grid place-items-center rounded-full shrink-0 active:scale-90 transition-transform disabled:opacity-40";

export function iconButtonStyle(variant: BtnVariant): React.CSSProperties {
  switch (variant) {
    case "primary":
      return {
        background: "var(--color-button)",
        color: "var(--color-button-text)",
      };
    case "danger":
      return {
        background:
          "color-mix(in srgb, var(--color-destructive) 14%, transparent)",
        color: "var(--color-destructive)",
        boxShadow:
          "inset 0 0 0 1px color-mix(in srgb, var(--color-destructive) 30%, transparent)",
      };
    default:
      return {
        background: "var(--color-secondary-bg)",
        color: "var(--color-text)",
        boxShadow: "inset 0 0 0 1px var(--surface-hairline)",
      };
  }
}

export function IconButton({
  label,
  onClick,
  disabled,
  variant = "default",
  size = 34,
  children,
}: {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  variant?: BtnVariant;
  size?: number;
  children: React.ReactNode;
}): React.ReactElement {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      onClick={onClick}
      disabled={disabled}
      className={BASE}
      style={{ ...iconButtonStyle(variant), width: size, height: size }}
    >
      {children}
    </button>
  );
}

export function IconLink({
  label,
  to,
  variant = "default",
  size = 34,
  children,
}: {
  label: string;
  to: string;
  variant?: BtnVariant;
  size?: number;
  children: React.ReactNode;
}): React.ReactElement {
  return (
    <Link
      aria-label={label}
      title={label}
      to={to}
      className={BASE}
      style={{ ...iconButtonStyle(variant), width: size, height: size }}
    >
      {children}
    </Link>
  );
}
